import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../auth/context/AuthContext';
import { Package, Users, Flame, Trophy, ShieldCheck, Star, Zap } from 'lucide-react';

const TIER_THEME = {
  bronze: { color: '#92400e', bg: '#fef3c7', border: '#f59e0b' },
  silver: { color: '#475569', bg: '#f1f5f9', border: '#94a3b8' },
  gold: { color: '#854d0e', bg: '#fef9c3', border: '#eab308' },
};

const SOURCE_LABEL = {
  shipping: { label: 'Shipping reward', Icon: Package, color: 'var(--brand-blue)' },
  referral: { label: 'Referral bonus', Icon: Users, color: 'var(--brand-blue)' },
  streak: { label: 'Streak bonus', Icon: Flame, color: '#d97706' },
  admin: { label: 'Admin adjustment', Icon: ShieldCheck, color: '#374151' },
};

function fmtDate(dateStr) {
  try {
    return new Date(dateStr).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

export default function LoyaltyPage() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);

  useEffect(() => {
    axios.get('/api/customer/loyalty/history')
      .then((r) => setHistory(r.data))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));

    axios.get('/api/customer/loyalty/settings')
      .then((r) => setSettings(r.data))
      .catch(() => {})
      .finally(() => setLoadingSettings(false));
  }, []);

  const tier = user?.loyalty_tier || 'bronze';
  const points = user?.loyalty_points || 0;

  const tiers = useMemo(() => {
    if (!settings) return [];

    return [
      {
        key: 'bronze',
        label: 'Bronze',
        min: settings.tiers.bronze.min_points,
        max: settings.tiers.silver.min_points - 1,
        discount: settings.tiers.bronze.discount_percent,
        perks: [
          `${settings.points_per_kg_air} points per kg shipped by air`,
          `${settings.points_per_cbm_sea} points per CBM shipped by sea`,
          `${settings.referral_points} points per referral`,
        ],
        ...TIER_THEME.bronze,
      },
      {
        key: 'silver',
        label: 'Silver',
        min: settings.tiers.silver.min_points,
        max: settings.tiers.gold.min_points - 1,
        discount: settings.tiers.silver.discount_percent,
        perks: [
          `${settings.tiers.silver.discount_percent}% shipping discount`,
          'All Bronze benefits',
          'Priority support handling',
        ],
        ...TIER_THEME.silver,
      },
      {
        key: 'gold',
        label: 'Gold',
        min: settings.tiers.gold.min_points,
        max: null,
        discount: settings.tiers.gold.discount_percent,
        perks: [
          `${settings.tiers.gold.discount_percent}% shipping discount`,
          'All Silver benefits',
          'Top priority handling',
        ],
        ...TIER_THEME.gold,
      },
    ];
  }, [settings]);

  const currentTier = tiers.find((entry) => entry.key === tier) || tiers[0];
  const nextTier = tiers[tiers.findIndex((entry) => entry.key === tier) + 1];
  const progress = nextTier ? Math.min((points / nextTier.min) * 100, 100) : 100;

  const howToEarn = settings ? [
    { Icon: Package, label: `${settings.points_per_kg_air} points per kg shipped by air`, color: 'var(--brand-blue)' },
    { Icon: Package, label: `${settings.points_per_cbm_sea} points per CBM shipped by sea`, color: 'var(--brand-blue)' },
    { Icon: Users, label: `${settings.referral_points} points per referral`, color: 'var(--brand-blue)' },
    { Icon: Flame, label: `${settings.streak_bonus_points} bonus points for shipping 3 months in a row`, color: '#d97706' },
  ] : [];

  return (
    <div>
      <div className="page-header">
        <Link to="/home" className="back-btn">&larr;</Link>
        <h1>Rewards</h1>
      </div>

      <div className="page-content">
        {loadingSettings ? (
          <div className="loading-center"><div className="spinner spinner-dark" /></div>
        ) : currentTier ? (
          <>
            <div className="card" style={{ background: currentTier.bg, border: `1.5px solid ${currentTier.border}`, textAlign: 'center' }}>
              <Trophy size={40} strokeWidth={1.6} style={{ color: currentTier.color, margin: '0 auto 8px' }} />
              <p style={{ fontSize: 20, fontWeight: 800, color: currentTier.color }}>{currentTier.label} Member</p>
              <p style={{ fontSize: 32, fontWeight: 900, color: 'var(--steel-soft)', marginTop: 8 }}>{points.toLocaleString()}</p>
              <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>Total Points</p>
              <p style={{ fontSize: 13, color: currentTier.color, fontWeight: 700, marginBottom: 12 }}>
                Current discount: {currentTier.discount}%
              </p>
              {nextTier ? (
                <div>
                  <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 20, height: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${progress}%`, background: 'var(--steel-soft)', height: '100%', borderRadius: 20, transition: 'width 0.5s' }} />
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
                    {(nextTier.min - points).toLocaleString()} more points to {nextTier.label}
                  </p>
                </div>
              ) : (
                <p style={{ fontSize: 13, color: currentTier.color, fontWeight: 600 }}>You&apos;re at the top tier!</p>
              )}
            </div>

            <p className="section-title" style={{ marginTop: 16 }}>How to Earn Points</p>
            {howToEarn.map(({ Icon, label, color }, index) => (
              <div key={index} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Icon size={22} strokeWidth={1.8} style={{ color, flexShrink: 0 }} />
                <p style={{ fontSize: 14, fontWeight: 600, color }}>{label}</p>
              </div>
            ))}

            <p className="section-title" style={{ marginTop: 16 }}>Tier Benefits</p>
            {tiers.map((entry) => (
              <div
                key={entry.key}
                className="card"
                style={{
                  border: entry.key === tier ? `2px solid ${entry.border}` : '1px solid var(--border)',
                  background: entry.key === tier ? entry.bg : 'var(--surface)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <Star size={20} strokeWidth={1.8} style={{ color: entry.color, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: 700, color: entry.color }}>{entry.label}</p>
                    <p style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {entry.min.toLocaleString()}{entry.max ? `–${entry.max.toLocaleString()}` : '+'} points
                    </p>
                  </div>
                  {entry.key === tier && (
                    <span style={{ background: entry.color, color: 'white', fontSize: 10, padding: '3px 8px', borderRadius: 20, fontWeight: 700 }}>
                      CURRENT
                    </span>
                  )}
                </div>
                <ul style={{ paddingLeft: 18 }}>
                  {entry.perks.map((perk, index) => (
                    <li key={index} style={{ fontSize: 13, color: 'var(--text)', marginBottom: 3 }}>{perk}</li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        ) : null}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
          <p className="section-title" style={{ marginTop: 0 }}>Points History</p>
          <Zap size={16} strokeWidth={1.8} style={{ color: 'var(--muted)' }} />
        </div>

        {loadingHistory ? (
          <div className="loading-center"><div className="spinner spinner-dark" /></div>
        ) : history.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 13, padding: 20 }}>
            No points earned yet. Ship something to get started!
          </div>
        ) : (
          history.map((entry) => {
            const info = SOURCE_LABEL[entry.source] || SOURCE_LABEL.admin;
            const { Icon } = info;
            const isPositive = entry.points > 0;
            return (
              <div key={entry.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
                <Icon size={18} strokeWidth={1.8} style={{ color: info.color, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{entry.note || info.label}</p>
                  <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{fmtDate(entry.created_at)}</p>
                </div>
                <p style={{ fontWeight: 700, fontSize: 14, color: isPositive ? '#16a34a' : '#dc2626', flexShrink: 0 }}>
                  {isPositive ? '+' : ''}{entry.points} pts
                </p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
